import { Controller } from "@hotwired/stimulus";

// Handles image load errors by falling back to an alternative URL
// (e.g., original Instagram CDN when S3 copy is archived in Glacier).
//
// For old media the fallback usually fails too: the Instagram CDN URL is
// captured at ingest and its signature expires long before anyone views the
// post again. Verified on prod — a 2024 deliverable returns 403 from both the
// S3 presigned URL (archive_status=ARCHIVE_ACCESS) and the CDN fallback. So
// when both fail we swap in a placeholder explaining the restore, rather than
// leaving a broken image with no explanation. PublicDeliverablesController
// queues RestoreGlacierObjectsJob on the same page load, so a restore really
// is in flight by the time this renders.
export default class extends Controller {
  static targets = ["image"];

  imageTargetConnected(img) {
    img.addEventListener("error", this.handleError);
  }

  imageTargetDisconnected(img) {
    img.removeEventListener("error", this.handleError);
  }

  handleError = (event) => {
    const img = event.target;
    const fallbackUrl = img.dataset.fallbackUrl;

    // Only attempt fallback once to avoid infinite loops
    if (fallbackUrl && !img.dataset.fallbackAttempted) {
      img.dataset.fallbackAttempted = "true";
      img.src = fallbackUrl;
      return;
    }

    this.showRestoringPlaceholder(img);
  };

  showRestoringPlaceholder(img) {
    if (img.dataset.placeholderShown) return;
    img.dataset.placeholderShown = "true";

    const placeholder = document.createElement("div");
    placeholder.className = "archived-media-placeholder";

    const icon = document.createElement("i");
    icon.className = "icon archive";
    placeholder.appendChild(icon);

    const message = document.createElement("span");
    message.textContent =
      "This post's media is archived. We're restoring it — check back in a few hours.";
    placeholder.appendChild(message);

    // Video cards overlay a play icon on the image; it makes no sense here.
    const play =
      img.parentElement && img.parentElement.querySelector("i.icon.play");
    if (play) play.remove();

    img.replaceWith(placeholder);
  }
}
